import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AlertService, AuthService, TokenService } from '../services';
import { environment as env } from 'src/environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(
        private authService: AuthService,
        private tokenService: TokenService,
        private alertService: AlertService
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.tokenService.getLocalToken(env.TokenKeys.AccessToken);

        let authRequest = request;
        if (token) {
            authRequest = this.addTokenHeader(request, token);
        }

        return next.handle(authRequest).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse) {
                    if (error.status === 401) {
                        return this.handle401Error(authRequest, next);
                    }
                    if (error.status === 403) {
                        this.alertService.error("forbidden");
                    }
                }
                return throwError(() => error);
            })
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.authService.refreshToken().pipe(
                switchMap(resp => {
                    this.isRefreshing = false;
                    if (resp && resp.code === 1) {
                        const newToken = resp.data.token;
                        this.tokenService.setLocalToken(env.TokenKeys.AccessToken, newToken);
                        this.tokenService.setLocalToken(env.TokenKeys.RefreshToken, resp.data.refreshToken);

                        this.refreshTokenSubject.next(newToken);

                        return next.handle(this.addTokenHeader(request, newToken));
                    } else {
                        this.authService.forceLogout();
                        return throwError(() => new Error('Refresh token failed'));
                    }
                }),
                catchError(error => {
                    this.isRefreshing = false;
                    this.authService.forceLogout();
                    return throwError(() => error);
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => next.handle(this.addTokenHeader(request, token!))),
                catchError(error => {
                    this.isRefreshing = false;  // Ensure flag resets if refresh handling fails
                    return throwError(() => error);
                })
            );
        }
    }
}
